import Axios, {
	type AxiosError,
	type AxiosRequestConfig,
	type AxiosResponse,
	type InternalAxiosRequestConfig,
} from "axios";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { t } from "i18next";
import type { ErrorResponseData } from "@/types/error.types";
import { getKeycloak } from "@/lib/keycloak";
import {
	getImpersonationTokenFromStore,
	useImpersonationStore,
} from "@/features/impersonation/lib/impersonation-store";

const AXIOS_INSTANCE = Axios.create({
	baseURL: import.meta.env.VITE_API_URL,
	paramsSerializer: {
		serialize: (params) => {
			const searchParams = new URLSearchParams();
			Object.entries(params).forEach(([key, value]) => {
				if (value === undefined || value === null) return;
				if (Array.isArray(value)) {
					value.forEach((item) => {
						if (item !== undefined && item !== null) {
							searchParams.append(key, String(item));
						}
					});
				} else {
					searchParams.append(key, String(value));
				}
			});
			return searchParams.toString();
		},
	},
});

/**
 * Remove the Authorization header (case-insensitive) before a request config is
 * attached to a Sentry event, so the bearer/impersonation token never leaves the
 * client in an error payload. See `.claude/rules/security.md`.
 */
function redactAuthorization(
	headers: unknown
): Record<string, unknown> | undefined {
	if (!headers || typeof headers !== "object") return undefined;
	return Object.fromEntries(
		Object.entries(headers as Record<string, unknown>).filter(
			([key]) => key.toLowerCase() !== "authorization"
		)
	);
}

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
	config.headers.Accept = "application/json";

	const impersonationToken = getImpersonationTokenFromStore();
	if (impersonationToken) {
		config.headers.Authorization = `Bearer ${impersonationToken}`;
		return config;
	}

	const keycloak = getKeycloak();

	// Check if Keycloak is initialized and authenticated
	if (keycloak.authenticated && keycloak.token) {
		config.headers.Authorization = `Bearer ${keycloak.token}`;
	}

	return config;
}

AXIOS_INSTANCE.interceptors.request.use(authRequestInterceptor);

AXIOS_INSTANCE.interceptors.response.use(
	(response: AxiosResponse) => {
		return response;
	},
	async (error: AxiosError) => {
		const errorResponseData = error.response?.data as
			| ErrorResponseData
			| undefined;
		const message =
			errorResponseData?.translatedMsg ||
			error.message ||
			"An unexpected error occurred";

		// Capture API errors to Sentry (excluding certain types)
		if (
			error.code !== "ERR_CANCELED" &&
			error.response?.status !== 401 && // Don't log auth errors
			error.response?.status !== 404 && // Don't log not found errors
			error.response?.status !== 403 // Don't log forbidden errors
		) {
			Sentry.captureException(error, {
				tags: {
					type: "api_error",
					status: error.response?.status,
					method: error.config?.method?.toUpperCase(),
				},
				contexts: {
					api: {
						url: error.config?.url,
						baseURL: error.config?.baseURL,
						method: error.config?.method,
						status: error.response?.status,
						statusText: error.response?.statusText,
						responseData: error.response?.data,
					},
				},
				extra: {
					config: {
						// Strip the Authorization header — it carries the bearer
						// (or impersonation) token and must never reach Sentry.
						headers: redactAuthorization(error.config?.headers),
						params: error.config?.params as Record<string, unknown> | undefined,
						data: error.config?.data as unknown,
					},
				},
			});
		}

		if (error.response?.status === 401) {
			if (getImpersonationTokenFromStore()) {
				// Impersonation expired — drop it and let the admin session
				// resume. Keycloak.js still holds the original token.
				useImpersonationStore.getState().clearImpersonation();
				toast.error(t("Impersonation session ended. Returning to admin."));
			} else {
				// Use Keycloak's login method instead of manual redirect
				const keycloak = getKeycloak();
				if (!keycloak.authenticated) {
					// This will redirect to Keycloak login page
					await keycloak.login({
						redirectUri: window.location.origin + window.location.pathname,
					});
				}
			}
		}

		return Promise.reject(new Error(message, { cause: errorResponseData }));
	}
);

export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
	const source = Axios.CancelToken.source();
	const promise = AXIOS_INSTANCE<T>({
		...config,
		// Allow baseURL override - if provided in config, it will be used instead of the default
		baseURL: config.baseURL || AXIOS_INSTANCE.defaults.baseURL,
		cancelToken: source.token,
	}).then(({ data }) => data);

	// @ts-expect-error - Adding cancel method to promise for React Query
	promise.cancel = () => {
		source.cancel("Query was cancelled");
	};

	return promise;
};

export type ErrorType<TError = unknown> = TError;
export type BodyType<TBody = unknown> = TBody;
